import {
  type CalendarView,
  REMINDER_MINUTES,
  ReminderMinutes,
  SchoolHolidayRegion,
  type Settings,
  STROKE_SIZE_OPTIONS,
  StrokeSize,
  schoolHolidayRegionNames,
  type UpdateSettings,
  type WeekStart,
} from "@ronin/contracts";
import { formatBytes } from "@ronin/core";
import {
  Button,
  Checkbox,
  Dialog,
  SegmentedControl,
  Select,
  type SelectOption,
  toast,
} from "@ronin/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useLocation, useNavigate } from "@tanstack/react-router";
import { BellRing, Loader2, LogOut, Trash2 } from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { api, authStatusQuery } from "../api.js";
import { NeedsConnection } from "../components/needs-connection.js";
import { ConfirmDialog } from "../components/notes/dialogs.js";
import { attachmentUsageQuery, useSettings } from "../components/settings/use-settings.js";
import { needsConnection, showError } from "../lib/errors.js";
import { isNative } from "../platform/native.js";
import { syncNow } from "../platform/offline/index.js";
import { useOfflineStore, useSyncing } from "../platform/offline/react.js";
import { serverUrl } from "../platform/server.js";
import { signOutDevice } from "../platform/session.js";
import { APP_VERSION } from "../version.js";

const appRoute = getRouteApi("/app");

const weekStarts: ReadonlyArray<{ value: WeekStart; label: string }> = [
  { value: "monday", label: "Monday" },
  { value: "sunday", label: "Sunday" },
];

const views: ReadonlyArray<{ value: CalendarView; label: string }> = [
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "day", label: "Day" },
];

/** Radix Select values cannot be empty, so "no school holidays" gets its own value. */
const OFF = "off";

const schoolHolidayOptions: SelectOption[] = [
  { value: OFF, label: "Off" },
  ...SchoolHolidayRegion.options
    .map((region) => ({ value: region, label: schoolHolidayRegionNames[region] }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

/** spec10 FR2: "Off" plus the allowed offsets; `zero` names the 0-minute choice. */
function reminderOptions(zero: string): SelectOption[] {
  return [
    { value: OFF, label: "Off" },
    ...REMINDER_MINUTES.map((minutes) => ({
      value: String(minutes),
      label: minutes === 0 ? zero : `${minutes} min before`,
    })),
  ];
}
const todoReminderOptions = reminderOptions("At due time");
const eventReminderOptions = reminderOptions("At start");

const parseReminder = (value: string) =>
  value === OFF ? null : ReminderMinutes.parse(Number(value));

/** Daily reminder: every half hour of the day. */
const dailyTimeOptions: SelectOption[] = [
  { value: OFF, label: "Off" },
  ...Array.from({ length: 48 }, (_, i) => {
    const time = `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`;
    return { value: time, label: time };
  }),
];

/** Every timezone this browser knows, plus the stored one if the browser spells it differently. */
/** spec13 FR2: the pen sizes in page units. */
const strokeSizeOptions: SelectOption[] = STROKE_SIZE_OPTIONS.map((size) => ({
  value: String(size),
  label: String(size),
}));

const pens = [
  { key: "canvasStrokeThin", title: "Thin pen" },
  { key: "canvasStrokeMedium", title: "Medium pen" },
  { key: "canvasStrokeThick", title: "Thick pen" },
] as const;

function timeZoneOptions(current: string): SelectOption[] {
  const zones = new Set([...Intl.supportedValuesOf("timeZone"), "UTC", current]);
  return [...zones]
    .sort((a, b) => a.localeCompare(b))
    .map((zone) => ({ value: zone, label: zone.replaceAll("_", " ") }));
}

export function SettingsPage() {
  const { query, update } = useSettings();
  const serverVersion = useQuery(authStatusQuery).data?.version;
  const save = (patch: UpdateSettings) => update.mutate(patch);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold text-foreground">Settings</h1>
      </header>

      <AccountSection />

      {needsConnection(query) ? (
        <NeedsConnection onRetry={() => void query.refetch()} />
      ) : (
        query.isPending && (
          <p className="flex items-center gap-2 py-12 text-sm text-dim-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading settings…
          </p>
        )
      )}
      {query.isError && !needsConnection(query) && (
        <p className="border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load your settings. Try reloading the page.
        </p>
      )}
      {query.data && <SettingsForm settings={query.data} onChange={save} />}

      <StorageSection />

      <Section label="About">
        <Row
          title="Version"
          labelIsGroup
          control={() => (
            <span className="flex flex-col items-end font-mono text-sm text-secondary-foreground">
              <span>Ronin {APP_VERSION}</span>
              {isNative && serverVersion && (
                <span className="text-xs text-muted-foreground">Server {serverVersion}</span>
              )}
            </span>
          )}
        />
      </Section>

      <SignOutButton />
    </div>
  );
}

function SettingsForm({
  settings,
  onChange,
}: {
  settings: Settings;
  onChange: (patch: UpdateSettings) => void;
}) {
  const zones = useMemo(() => timeZoneOptions(settings.timezone), [settings.timezone]);

  return (
    <>
      <Section label="General">
        <Row
          title="Timezone"
          description="Dates and times are shown in this timezone."
          control={(id) => (
            <Select
              id={id}
              value={settings.timezone}
              options={zones}
              onValueChange={(timezone) => onChange({ timezone })}
              className="sm:w-64"
            />
          )}
        />
      </Section>

      <Section label="Calendar">
        <Row
          title="Week starts on"
          control={(id) => (
            <SegmentedControl
              aria-labelledby={id}
              value={settings.weekStart}
              options={weekStarts}
              onValueChange={(weekStart) => onChange({ weekStart })}
            />
          )}
          labelIsGroup
        />
        <Row
          title="Default view"
          description="The view the calendar opens with."
          control={(id) => (
            <SegmentedControl
              aria-labelledby={id}
              value={settings.calendarDefaultView}
              options={views}
              onValueChange={(calendarDefaultView) => onChange({ calendarDefaultView })}
            />
          )}
          labelIsGroup
        />
        <Row
          title="Public holidays"
          description="Show Austrian public holidays."
          control={(id) => (
            <Checkbox
              id={id}
              checked={settings.showPublicHolidays}
              onCheckedChange={(checked) => onChange({ showPublicHolidays: checked === true })}
            />
          )}
        />
        <Row
          title="School holidays"
          description="Show the school holidays of one state."
          control={(id) => (
            <Select
              id={id}
              value={settings.schoolHolidayRegion ?? OFF}
              options={schoolHolidayOptions}
              onValueChange={(value) =>
                onChange({
                  schoolHolidayRegion: value === OFF ? null : SchoolHolidayRegion.parse(value),
                })
              }
              className="sm:w-64"
            />
          )}
        />
      </Section>

      <Section label="Canvas">
        {pens.map(({ key, title }, i) => (
          <Row
            key={key}
            title={title}
            description={
              i === 0
                ? "Line width in page units (the page is 1000 wide). Changes existing drawings too."
                : undefined
            }
            control={(id) => (
              <Select
                id={id}
                value={String(settings[key])}
                options={strokeSizeOptions}
                onValueChange={(value) => onChange({ [key]: StrokeSize.parse(Number(value)) })}
                className="sm:w-32"
              />
            )}
          />
        ))}
      </Section>

      <NotificationsSection settings={settings} onChange={onChange} />
    </>
  );
}

/** spec10 FR2: reminders through the server's ntfy topic, plus a test message. */
function NotificationsSection({
  settings,
  onChange,
}: {
  settings: Settings;
  onChange: (patch: UpdateSettings) => void;
}) {
  const available = settings.notificationsAvailable;
  const test = useMutation({
    mutationFn: () => api.settings.testNotification(),
    onSuccess: () => toast.success("Test notification sent."),
    onError: showError("the test notification", "sent"),
  });
  const reminderValue = (minutes: number | null) => (minutes === null ? OFF : String(minutes));
  // A daily time saved outside the half-hour grid still shows as selected.
  const dailyOptions =
    settings.dailyReminderTime === null ||
    dailyTimeOptions.some((o) => o.value === settings.dailyReminderTime)
      ? dailyTimeOptions
      : [
          ...dailyTimeOptions,
          { value: settings.dailyReminderTime, label: settings.dailyReminderTime },
        ];

  return (
    <Section label="Notifications">
      {!available && (
        <p className="px-4 py-3.5 text-sm text-muted-foreground">
          Notifications are not set up on this server.
        </p>
      )}
      <Row
        title="Notifications"
        description="Reminders are sent to this server's ntfy topic."
        control={(id) => (
          <Checkbox
            id={id}
            disabled={!available}
            checked={settings.notificationsEnabled}
            onCheckedChange={(checked) => onChange({ notificationsEnabled: checked === true })}
          />
        )}
      />
      <Row
        title="Todos with a time"
        control={(id) => (
          <Select
            id={id}
            disabled={!available}
            value={reminderValue(settings.todoReminderMinutes)}
            options={todoReminderOptions}
            onValueChange={(value) => onChange({ todoReminderMinutes: parseReminder(value) })}
            className="sm:w-64"
          />
        )}
      />
      <Row
        title="Events with a time"
        control={(id) => (
          <Select
            id={id}
            disabled={!available}
            value={reminderValue(settings.eventReminderMinutes)}
            options={eventReminderOptions}
            onValueChange={(value) => onChange({ eventReminderMinutes: parseReminder(value) })}
            className="sm:w-64"
          />
        )}
      />
      <Row
        title="Daily reminder at"
        description="For todos without a time and all-day events, on their day."
        control={(id) => (
          <Select
            id={id}
            disabled={!available}
            value={settings.dailyReminderTime ?? OFF}
            options={dailyOptions}
            onValueChange={(value) => onChange({ dailyReminderTime: value === OFF ? null : value })}
            className="sm:w-64"
          />
        )}
      />
      <Row
        title="Test notification"
        description="Sends one message now, also while notifications are off."
        labelIsGroup
        control={() => (
          <Button
            variant="secondary"
            disabled={!available || test.isPending}
            onClick={() => test.mutate()}
          >
            <BellRing />
            Send test notification
          </Button>
        )}
      />
    </Section>
  );
}

/**
 * Who is signed in (spec02 FR1a). `/settings#account` scrolls it into view and focuses it.
 */
function AccountSection() {
  const { user } = appRoute.useRouteContext();
  const hash = useLocation({ select: (location) => location.hash });
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (hash === "account") {
      ref.current?.scrollIntoView({ block: "start" });
      ref.current?.focus({ preventScroll: true });
    }
  }, [hash]);

  return (
    <section
      ref={ref}
      id="account"
      tabIndex={-1}
      aria-labelledby="account-heading"
      className="flex scroll-mt-8 flex-col gap-2 outline-none"
    >
      <h2
        id="account-heading"
        className="px-1 text-xs font-semibold uppercase tracking-wide text-dim-foreground"
      >
        Account
      </h2>
      <div className="flex items-center gap-4 border bg-card px-4 py-3.5">
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold uppercase text-secondary-foreground"
        >
          {user.username.slice(0, 1)}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-foreground">{user.username}</span>
          <span className="truncate text-xs text-muted-foreground">{user.email}</span>
          {isNative && (
            <span className="truncate text-xs text-faint-foreground">
              {new URL(serverUrl()).host}
            </span>
          )}
        </div>
      </div>
      <PendingSync />
    </section>
  );
}

/** spec11 FR13: offline note changes still waiting for the server, and "Sync now". */
function PendingSync() {
  const store = useOfflineStore();
  const syncing = useSyncing();
  const pending = store?.pendingCount() ?? 0;
  if (pending === 0) return null;
  return (
    <div className="flex items-center gap-3 border bg-card px-4 py-3">
      <span role="status" className="min-w-0 flex-1 text-xs text-muted-foreground">
        {pending === 1 ? "1 note waiting to sync" : `${pending} notes waiting to sync`}
      </span>
      <Button variant="secondary" size="sm" disabled={syncing} onClick={() => void syncNow()}>
        {syncing ? "Syncing…" : "Sync now"}
      </Button>
    </div>
  );
}

/** The only Sign out (spec02 FR1a), at the bottom of the page under the version. */
function SignOutButton() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store = useOfflineStore();
  const [confirming, setConfirming] = useState(false);
  const pending = store?.pendingCount() ?? 0;

  async function signOut() {
    if (isNative) {
      // spec08 FR8: the device forgets everything even when the server cannot be reached.
      if (!(await signOutDevice(queryClient))) {
        toast("Signed out on this device. The server session ends on its own.");
      }
    } else {
      await api.auth.signOut();
      queryClient.clear();
    }
    await navigate({ to: "/login" });
  }

  return (
    <>
      <Button
        variant="secondary"
        className="w-full sm:w-fit"
        // spec11 FR15: unsent offline changes are lost on sign-out, so it asks first.
        onClick={() => (pending > 0 ? setConfirming(true) : void signOut())}
      >
        <LogOut />
        Sign out
      </Button>
      <ConfirmDialog
        open={confirming}
        title="Sign out?"
        description={`${pending === 1 ? "1 note has" : `${pending} notes have`} not synced yet. Signing out deletes ${pending === 1 ? "it" : "them"} from this device.`}
        confirmLabel="Sign out anyway"
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void signOut();
        }}
      />
    </>
  );
}

/** Attachment storage (spec05 FR4): used space and "Delete unused attachments". */
function StorageSection() {
  const queryClient = useQueryClient();
  const usage = useQuery(attachmentUsageQuery);
  const [confirming, setConfirming] = useState(false);
  const deleteUnused = useMutation({
    mutationFn: () => api.attachments.deleteUnused(),
    onSuccess: ({ count, bytes }) => {
      toast.success(
        count === 0
          ? "No unused attachments."
          : `Deleted ${count} unused ${count === 1 ? "file" : "files"} (${formatBytes(bytes)}).`,
      );
      void queryClient.invalidateQueries({ queryKey: attachmentUsageQuery.queryKey });
    },
    onError: showError("the clean-up", "done"),
  });

  const data = usage.data;
  return (
    <Section label="Storage">
      <Row
        title="Attachments"
        description={
          data
            ? `${formatBytes(data.bytes)} of ${formatBytes(data.quotaBytes)} used · ${data.count} ${data.count === 1 ? "file" : "files"}`
            : usage.isError
              ? "Could not load the storage use."
              : "Loading…"
        }
        labelIsGroup
        control={(id) =>
          data ? (
            <progress
              aria-labelledby={id}
              max={data.quotaBytes}
              value={data.bytes}
              className="w-full sm:w-48"
            />
          ) : null
        }
      />
      <Row
        title="Delete unused attachments"
        description="Files that no note (including the trash) links to."
        labelIsGroup
        control={() => (
          <Button
            variant="secondary"
            disabled={deleteUnused.isPending}
            onClick={() => setConfirming(true)}
          >
            <Trash2 />
            Delete unused
          </Button>
        )}
      />
      <Dialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete unused attachments?"
        description="Files no note links to are deleted for good. Files uploaded in the last hour are kept."
      >
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirming(false)} data-autofocus>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setConfirming(false);
              deleteUnused.mutate();
            }}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}

/** Section label (DESIGN.md typography) above one panel of setting rows. */
function Section({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h2
        id={id}
        className="px-1 text-xs font-semibold uppercase tracking-wide text-dim-foreground"
      >
        {label}
      </h2>
      <div className="flex flex-col divide-y border bg-card">{children}</div>
    </section>
  );
}

/**
 * One setting: title and description on the left, the control on the right (stacked on
 * phones). The title labels the control; for a radio group it names the group instead.
 */
function Row({
  title,
  description,
  control,
  labelIsGroup,
}: {
  title: string;
  description?: string;
  control: (id: string) => ReactNode;
  labelIsGroup?: boolean;
}) {
  const id = useId();
  const titleClass = "text-sm font-medium text-foreground";
  return (
    <div className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex min-w-0 flex-col gap-0.5">
        {labelIsGroup ? (
          <span id={id} className={titleClass}>
            {title}
          </span>
        ) : (
          <label htmlFor={id} className={titleClass}>
            {title}
          </label>
        )}
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <div className="shrink-0">{control(id)}</div>
    </div>
  );
}
